<template>
  <dialog-reset
    title="配件出库登记"
    :visible.sync="visible"
    width="600px"
    top="10vh"
    height="auto"
    :close-on-click-modal="false"
    :zoomShow="false"
    @close="handleClose"
  >
    <div class="part-info">
      <div class="info-item">
        <div class="info-label">配件编码</div>
        <div class="info-value">{{ partData.partCode || '-' }}</div>
      </div>
      <div class="info-item">
        <div class="info-label">配件名称</div>
        <div class="info-value">{{ partData.partName || '-' }}</div>
      </div>
      <div class="info-item">
        <div class="info-label">当前库存</div>
        <div class="info-value stock-warning">{{ partData.stockCount || 0 }} {{ partData.unit || '个' }}</div>
      </div>
      <div class="info-item">
        <div class="info-label">最低库存</div>
        <div class="info-value">{{ partData.minimumStock || 0 }} {{ partData.unit || '个' }}</div>
      </div>
    </div>

    <el-alert
      v-if="willBelowSafetyStock"
      type="warning"
      :closable="false"
      show-icon
      style="margin: 16px 0"
    >
      注意: 出库后库存将低于安全库存
    </el-alert>

    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      label-width="120px"
      size="small"
      style="margin-top: 20px"
    >
      <el-form-item label="出库数量" prop="quantity">
        <el-input-number
          v-model="form.quantity"
          :min="0.01"
          :max="maxOutboundQuantity"
          :precision="2"
          :step="1"
          placeholder="请输入出库数量"
          style="width: 100%"
        />
        <div v-if="form.quantity > maxOutboundQuantity" class="error-tip">
          出库数量不能超过当前库存
        </div>
      </el-form-item>
      <el-form-item label="出库类型" prop="outboundType">
        <el-select v-model="form.outboundType" placeholder="请选择出库类型" style="width: 100%">
          <el-option
            v-for="item in outboundTypeOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="领用人" prop="recipient">
        <div style="display: flex; align-items: center; gap: 8px">
          <el-select
            v-model="form.recipientType"
            style="width: 150px"
            @change="handleRecipientTypeChange"
          >
            <el-option label="选择员工" value="employee" />
            <el-option label="输入姓名" value="name" />
          </el-select>
          <el-select
            v-if="form.recipientType === 'employee'"
            v-model="form.recipient"
            placeholder="请选择员工"
            filterable
            remote
            :remote-method="searchEmployees"
            :loading="employeeLoading"
            style="flex: 1"
          >
            <el-option
              v-for="item in employeeOptions"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
          <el-input
            v-else
            v-model="form.recipient"
            placeholder="输入姓名"
            maxlength="50"
            style="flex: 1"
          />
        </div>
      </el-form-item>
      <el-form-item label="领用用途" prop="purpose">
        <el-input
          v-model="form.purpose"
          type="textarea"
          :rows="3"
          placeholder="简要说明领用目的"
          maxlength="200"
          show-word-limit
        />
      </el-form-item>
      <el-form-item label="备注">
        <el-input
          v-model="form.remark"
          type="textarea"
          :rows="3"
          placeholder="填写其他说明"
          maxlength="200"
          show-word-limit
        />
      </el-form-item>
    </el-form>

    <div class="stock-change-info">
      <el-alert
        :closable="false"
        show-icon
        type="info"
      >
        出库后库存变化: {{ partData.stockCount || 0 }} - {{ form.quantity || 0 }} = {{ afterStock }} {{ partData.unit || '个' }}
      </el-alert>
    </div>

    <span slot="dialog_footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" :loading="submitting" @click="handleSubmit">确认出库</el-button>
    </span>
  </dialog-reset>
</template>

<script>
import { outbound } from '../api'
import request from '@/router/axios'

export default {
  name: 'OutboundDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    partData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      submitting: false,
      employeeLoading: false,
      employeeOptions: [],
      form: {
        partId: null,
        quantity: null,
        outboundType: '',
        recipientType: 'employee',
        recipient: '',
        purpose: '',
        remark: ''
      },
      rules: {
        quantity: [
          { required: true, message: '请输入出库数量', trigger: 'blur' },
          { type: 'number', min: 0.01, message: '出库数量必须大于0', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (value > this.maxOutboundQuantity) {
                callback(new Error('出库数量不能超过当前库存'))
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ],
        outboundType: [
          { required: true, message: '请选择出库类型', trigger: 'change' }
        ],
        recipient: [
          { required: true, message: '请选择或输入领用人', trigger: 'change' }
        ],
        purpose: [
          { required: true, message: '请输入领用用途', trigger: 'blur' }
        ]
      },
      outboundTypeOptions: [
        { label: '领用出库', value: 'use' },
        { label: '调拨出库', value: 'transfer' },
        { label: '报废出库', value: 'scrap' },
        { label: '盘亏出库', value: 'inventory_loss' },
        { label: '其他', value: 'other' }
      ]
    }
  },
  computed: {
    maxOutboundQuantity() {
      return this.partData.stockCount || 0
    },
    afterStock() {
      const current = this.partData.stockCount || 0
      const quantity = this.form.quantity || 0
      return Math.max(0, current - quantity)
    },
    willBelowSafetyStock() {
      const current = this.partData.stockCount || 0
      const minimum = this.partData.minimumStock || 0
      const quantity = this.form.quantity || 0
      return current - quantity < minimum
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.form.partId = this.partData.id
        this.form.quantity = null
        this.form.outboundType = ''
        this.form.recipientType = 'employee'
        this.form.recipient = ''
        this.form.purpose = ''
        this.form.remark = ''
        this.employeeOptions = []
        this.$nextTick(() => {
          this.$refs.form && this.$refs.form.clearValidate()
        })
      }
    }
  },
  methods: {
    handleRecipientTypeChange() {
      this.form.recipient = ''
      this.$refs.form && this.$refs.form.clearValidate()
    },
    async searchEmployees(query) {
      if (!query) {
        this.employeeOptions = []
        return
      }
      this.employeeLoading = true
      try {
        const res = await request({
          url: '/api/blade-user/select/user',
          method: 'get',
          params: { realName: query }
        })
        if (res.data && res.data.code === 200) {
          this.employeeOptions = res.data.data || []
        }
      } catch (e) {
        console.error('搜索员工失败', e)
      } finally {
        this.employeeLoading = false
      }
    },
    handleSubmit() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          if (this.form.quantity > this.maxOutboundQuantity) {
            this.$message.warning('出库数量不能超过当前库存')
            return
          }
          this.submitting = true
          outbound({
            id: this.partData.id,
            stockCount: this.partData.stockCount || 0,
            outboundCount: this.form.quantity
          }).then(() => {
            this.$message.success('出库成功')
            this.$emit('success')
            this.handleClose()
          }).catch(() => {
            this.submitting = false
          })
        }
      })
    },
    handleClose() {
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped lang="scss">
.part-info {
  display: flex;
  flex-wrap: wrap;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  .info-item {
    width: 50%;
    margin-bottom: 12px;
    .info-label {
      font-size: 12px;
      color: #999;
      margin-bottom: 4px;
    }
    .info-value {
      font-size: 14px;
      color: #333;
      font-weight: 500;
      &.stock-warning {
        color: #e6a23c;
      }
    }
  }
}
.error-tip {
  color: #f56c6c;
  font-size: 12px;
  margin-top: 4px;
}
.stock-change-info {
  margin-top: 16px;
}
</style>




